import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@kmgestao/ui';

import type { FormField, FormSchema } from '../schemas.js';

import { FormFieldRenderer } from './FormFieldRenderer.js';
import { FormSection } from './FormSection.js';
import { useAutofillMapping } from './hooks/useAutofillMapping.js';
import { useFormSubmit } from './hooks/useFormSubmit.js';
import { buildAnswersValidator } from './logic/validate.js';

export interface FormRendererProps {
  /** Schema do template (vindo de `form_templates.schema`). */
  schema: FormSchema;
  /** Respostas iniciais (prefill do lead, partial_answers do auto-save). */
  initialAnswers?: Record<string, unknown>;
  /** Chamado a cada mudança de resposta — consumidor faz auto-save
   * debounced (M1-01b-ii.b). Passa o objeto completo atual. */
  onChange?: (answers: Record<string, unknown>) => void;
  /** Chamado no submit final após validação Zod. */
  onSubmit: (answers: Record<string, unknown>) => Promise<void> | void;
  /** Desabilita o form inteiro (submissão concluída, expirada, etc). */
  disabled?: boolean;
  /** Prefixo de IDs para evitar colisão se múltiplos forms
   * aparecerem na mesma página (raro, mas defensivo). */
  idPrefix?: string;
  /** Label do botão de submit. Default: "Enviar respostas". */
  submitLabel?: string;
  /** Slot antes dos campos (ex: título customizado). */
  header?: ReactNode;
  /** Slot após o botão de submit (ex: aviso LGPD). */
  footer?: ReactNode;
}

/**
 * Componente principal do subsistema de Forms. Renderiza qualquer
 * template `FormSchema` em página pública ou privada — mobile-first,
 * schema-driven, sem acoplamento a um contexto específico (lead,
 * clínica, paciente). Ver `docs/forms-subsystem.md`.
 *
 * **Estado controlado** (`answers`): gerenciado internamente,
 * notifica o consumidor via `onChange` para persistência externa
 * (auto-save). No submit, valida via Zod dinâmico (que respeita
 * `visible_if` — campos ocultos viram optional) e chama `onSubmit`.
 *
 * **Autofill** (CNPJ/CEP): `CnpjField`/`CepField` despacham para os
 * callbacks de `useAutofillMapping`, que traduzem o objeto da
 * BrasilAPI para IDs convencionais do schema. Ver hook para detalhes
 * da heurística de mapeamento.
 *
 * **Divisão** (AGENTS.md §5.1 — arquivos ≤ 200 linhas):
 *   - Render de campo individual: `FormFieldRenderer.tsx`
 *   - Lógica de autofill: `hooks/useAutofillMapping.ts`
 *   - Lógica de submit + erros: `hooks/useFormSubmit.ts`
 *   - Validação Zod dinâmica: `logic/validate.ts`
 *   - Visibilidade condicional: `logic/visibleIf.ts`
 */
export function FormRenderer({
  schema,
  initialAnswers,
  onChange,
  onSubmit,
  disabled = false,
  idPrefix = 'form',
  submitLabel = 'Enviar respostas',
  header,
  footer,
}: FormRendererProps) {
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => ({
    ...(initialAnswers ?? {}),
  }));

  // Validador reconstruído a cada mudança de answers — visibilidade
  // condicional depende das respostas atuais (campo oculto vira optional).
  const validator = useMemo(() => buildAnswersValidator(schema, answers), [schema, answers]);

  // Achata todas as perguntas (incluindo follow_up + other_field) por
  // ID. Usado para validação de visibilidade e aplicação de autofill.
  const allFields = useMemo(() => {
    const acc = new Map<string, FormField>();
    for (const section of schema.sections) {
      for (const q of section.questions) {
        acc.set(q.id, q);
        if (q.follow_up) acc.set(q.follow_up.id, q.follow_up);
        if (q.other_field) acc.set(q.other_field.id, q.other_field);
      }
    }
    return acc;
  }, [schema]);

  const { submitting, errors, setErrors, handleSubmit } = useFormSubmit({
    allFields,
    answers,
    validator,
    onSubmit,
    idPrefix,
    disabled,
  });

  const updateAnswer = useCallback(
    (fieldId: string, value: unknown) => {
      setAnswers((prev) => {
        const next = { ...prev, [fieldId]: value };
        onChange?.(next);
        return next;
      });
      // Limpa erro desse campo ao editar.
      setErrors((prev) => {
        if (!prev[fieldId]) return prev;
        const { [fieldId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [onChange, setErrors],
  );

  const { applyCnpjAutofill, applyCepAutofill } = useAutofillMapping({
    allFields,
    setAnswers,
    notifyChange: onChange,
  });

  const fieldDisabled = disabled || submitting;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      {header}

      {schema.sections.map((section) => (
        <FormSection key={section.id} section={section}>
          {section.questions.map((field) => (
            <FormFieldRenderer
              key={field.id}
              field={field}
              answers={answers}
              errors={errors}
              onChangeField={updateAnswer}
              onCnpjAutofill={applyCnpjAutofill}
              onCepAutofill={applyCepAutofill}
              disabled={fieldDisabled}
              idPrefix={idPrefix}
            />
          ))}
        </FormSection>
      ))}

      <div className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 p-4 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none">
        <Button
          type="submit"
          size="lg"
          className="w-full sm:w-auto"
          disabled={disabled || submitting}
        >
          {submitting ? 'Enviando…' : submitLabel}
        </Button>
      </div>

      {footer}
    </form>
  );
}
